import jsPDF from "jspdf"
import { SystemConstants } from "src/common/constants"
import { Company } from "src/common/models/company"
import { ServiceContractPayment } from "src/common/models/serviceContractDebitPayment"
import { applyPlugin } from 'jspdf-autotable'
import { NumberToWords } from "src/common/numberToWords"
import { formatToCurrency, formatToDisplayDatetime } from "src/common/util"
import { SystemDescriptions } from "src/common/descriptions"

applyPlugin(jsPDF)

interface Props {
    company: Company
    payment: ServiceContractPayment
    behavior: "PREVIEW" | "SAVE"
}

interface ImageData {
    base64: string
    format: string
}

async function getBase64FromUrl(url): Promise<ImageData> {
    const response = await fetch(url);
    const blob = await response.blob();

    const mimeType = blob.type;
    let imageFormat = "JPEG";

    if (mimeType === "image/png") {
        imageFormat = "PNG";
    }

    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve({
            base64: reader.result as string,
            format: imageFormat.toLowerCase()
        });
    });
}

const CONSTANTS = {
    HEADER_X_START: 13,
    DETAIL_X_START: 60,
    FOOTER_X_START: 240,
}

const ContractPaymentVoucher = async (props: Props) => {
    const doc = new jsPDF();

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CONTRACT_PAYMENTS

    const fontConstants = SystemConstants.REPORTS.FONT

    const logoObject: ImageData | undefined = props.company.logoUrl && props.company.logoUrl.length > 0
        ? await getBase64FromUrl(props.company.logoUrl) : undefined;

    const divider = (yAxis: number) => {
        doc.setLineWidth(0.1);
        doc.setDrawColor(200, 200, 200);
        doc.line(10, yAxis, 200, yAxis)
    }

    const printRightTextAligned = (text: string, yAxis: number) => {
        const pageWidth = doc.internal.pageSize.width;
        const textWidth = doc.getTextWidth(text);

        const xAxis = pageWidth - textWidth - 10;
        doc.text(text, xAxis, yAxis)
    }

    const buildHeader = () => {
        doc.setProperties({
            title: descriptions.TITLE,
        })

        if (logoObject) {
            doc.addImage(
                logoObject.base64,
                logoObject.format,
                13,
                CONSTANTS.HEADER_X_START,
                20,
                20,
            )
        }

        doc.setFontSize(fontConstants.SIZE);
        doc.setFont(fontConstants.FAMILY, fontConstants.BOLD);
        printRightTextAligned(props.company.name, CONSTANTS.HEADER_X_START + 5)

        doc.setFont(fontConstants.FAMILY, fontConstants.NORMAL);
        printRightTextAligned(props.company.slogan, CONSTANTS.HEADER_X_START + 10)
        printRightTextAligned(props.company.address + " " + props.company.city + " " + props.company.state, CONSTANTS.HEADER_X_START + 15)
        printRightTextAligned(props.company.phone + " - " + props.company.email, CONSTANTS.HEADER_X_START + 20)

        divider(CONSTANTS.HEADER_X_START + 25)

        doc.setFont(fontConstants.FAMILY, fontConstants.BOLD);
        doc.text(descriptions.VOUCHER.TITLE, 13, CONSTANTS.HEADER_X_START + 30)
        doc.text(descriptions.VOUCHER.RECEIVED_FROM, 13, CONSTANTS.HEADER_X_START + 35)
        doc.text(descriptions.VOUCHER.DATETIME, 140, CONSTANTS.HEADER_X_START + 35)
        doc.text(descriptions.VOUCHER.ADDRESS, 13, CONSTANTS.HEADER_X_START + 40)

        doc.setFont(fontConstants.FAMILY, fontConstants.NORMAL);
        doc.text(props.payment.customer || '', 37, CONSTANTS.HEADER_X_START + 35)
        printRightTextAligned(formatToDisplayDatetime(props.payment.datetime), CONSTANTS.HEADER_X_START + 35)
        doc.text('Ciudad', 37, CONSTANTS.HEADER_X_START + 40)

        divider(CONSTANTS.HEADER_X_START + 45)
    }

    const buildDetailData = () => {

        const itemDetailsRows = [[
            "1",
            props.payment.description,
            formatToCurrency(props.payment.amount.toString()),
        ]
        ];

        itemDetailsRows.push([
            "",
            descriptions.VOUCHER.LAST_ITEM,
            "",
        ])

        const itemDetailsHeaders = [
            descriptions.VOUCHER.KEY,
            descriptions.VOUCHER.DESCRIPTION,
            descriptions.VOUCHER.TOTAL,
        ];

        const columnWidths = [15, 147, 30];

        const headerStyles = {
            fillColor: [240, 240, 240],
            textColor: [0],
            fontFamily: fontConstants.FAMILY,
            fontStyle: fontConstants.BOLD,
        };

        doc.setFont(fontConstants.FAMILY);

        const itemDetailsYStart = CONSTANTS.DETAIL_X_START;

        (doc as any).autoTable({
            head: [itemDetailsHeaders],
            body: itemDetailsRows,
            styles: { cellWidth: "wrap" },
            startY: itemDetailsYStart,
            headStyles: {
                fillColor: headerStyles.fillColor,
                textColor: headerStyles.textColor,
                fontStyle: headerStyles.fontStyle,
                fontSize: fontConstants.SIZE,
                font: fontConstants.FAMILY,
                halign: 'left',
            },
            columnStyles: {
                0: { cellWidth: columnWidths[0] },
                1: { cellWidth: columnWidths[1] },
                2: { cellWidth: columnWidths[2] },
                3: { cellWidth: columnWidths[3] },
                4: { cellWidth: columnWidths[4] },
            },
            alternateRowStyles: { fillColor: [255, 255, 255] },
            bodyStyles: {
                fontSize: fontConstants.SIZE,
                font: fontConstants.FAMILY,
                cellPadding: { top: 1, right: 5, bottom: 1, left: 2 },
                textColor: [0, 0, 0],
                rowPageBreak: 'avoid',
            },
            margin: { top: 10, left: 13 },
        });

    }

    const buildFooter = () => {
        const converter = new NumberToWords()
        doc.setFont(fontConstants.FAMILY, fontConstants.NORMAL)
        doc.text(`${descriptions.VOUCHER.TOTAL_AS_WORDS} ${converter.convertNumber(props.payment.amount, "quetzales")} `, 13, CONSTANTS.FOOTER_X_START)

        doc.setFont(fontConstants.FAMILY, fontConstants.BOLD)
        doc.text(descriptions.VOUCHER.CASH, 150, CONSTANTS.FOOTER_X_START)
        doc.text(descriptions.VOUCHER.TRANSACTION, 150, CONSTANTS.FOOTER_X_START + 5)
        doc.text(descriptions.VOUCHER.OTHER, 150, CONSTANTS.FOOTER_X_START + 10)
        doc.text(descriptions.VOUCHER.TOTAL, 150, CONSTANTS.FOOTER_X_START + 15)

        doc.setFont(fontConstants.FAMILY, fontConstants.NORMAL)
        printRightTextAligned(formatToCurrency(props.payment.cash.toString(), "Q"), CONSTANTS.FOOTER_X_START)
        printRightTextAligned(formatToCurrency(props.payment.transaction.toString(), "Q"), CONSTANTS.FOOTER_X_START + 5)
        printRightTextAligned(formatToCurrency(props.payment.disscount.toString(), "Q"), CONSTANTS.FOOTER_X_START + 10)

        doc.setFont(fontConstants.FAMILY, fontConstants.BOLD)
        printRightTextAligned(formatToCurrency(props.payment.amount.toString(), "Q"), CONSTANTS.FOOTER_X_START + 15)
        // doc.text(formatToCurrency(props.payment.amount.toString()), 175, 225)

        // doc.line(10, 230, 200, 230)
        divider(CONSTANTS.FOOTER_X_START + 20)

        doc.setFont(fontConstants.FAMILY, fontConstants.NORMAL)
        doc.text(`${props.company.name}, ${props.company.slogan}`, 13, CONSTANTS.FOOTER_X_START + 25)
        // doc.text(`${descriptions.VOUCHER.PRINTED_AT} ${dayjs().format(SystemConstants.DATETIME_FORMAT_DISPLAY)} `, 13, CONSTANTS.FOOTER_X_START + 30)
    }

    const showReport = () => {
        if (props.behavior === "PREVIEW") {
            const pdfDataUri = doc.output('datauristring');
            const newTab = window.open();
            newTab?.document.write(`<iframe width='100%' height='100%' src='${pdfDataUri}'></iframe>`);
        } else {
            doc.save(`${props.payment.customer?.replace(" ", "-")}-${props.payment.id}-voucher.pdf`)
        }
    }

    buildHeader()
    buildDetailData()
    buildFooter()
    showReport()
}

export default ContractPaymentVoucher